import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";

import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { ShrimpBaseline, ShrimpGrowingStageData } from "../../../../models/Baseline/ShrimpBaseline";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { ShrimpIntervention } from "../../../../models/Intervention/ShrimpIntervention";
import { formatLabelName } from "../../../../utils/string-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";

interface ShrimpFertilizerFormProps {
  stageIndex?: number | undefined;
  itemIndex?: number | undefined;
  formType: FormType;
  required?: boolean;
  baseline: ShrimpBaseline;
  editEnabled: boolean;
}

const ShrimpFertilizerForm: FC<ShrimpFertilizerFormProps> = ({
  stageIndex = 0,
  itemIndex = 0,
  formType,
  required = false,
  editEnabled = true,
  baseline,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? unitLong(userUOM.unitBarnOutputMass)
    : "kg";

  const propName = "fertilizerTypes";
  const fieldItemPrefix = `stages.${stageIndex}.stageData.operations.${propName}.${itemIndex}`;

  const fc = useFormContext<ShrimpIntervention>();

  const fertilizerData =
    (baseline?.stages[stageIndex]?.stageData as ShrimpGrowingStageData)?.operations?.fertilizerTypes[
    itemIndex
    ];

  return (
    <>
      <input
        ref={fc.register()}
        type="hidden"
        name={`${fieldItemPrefix}.id`}
        value={fertilizerData?.id}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${fieldItemPrefix}.type`}
        value={fertilizerData?.type}
      />
      {!editEnabled && (fc.getValues(`${fieldItemPrefix}.amount`) === undefined ||
        fc.getValues(`${fieldItemPrefix}.amount`) === "") ? (
        <DsmGrid className={classes.dsmGridTwoColumn}>
          <ReactHookDsmInput
            name={`${fieldItemPrefix}.originalAmount`}
            label={formatLabelName(fertilizerData?.type as string)}
            tooltip={intl.formatMessage({
              id: "BASELINE.FORM.OPERATIONS.SHRIMP.FERTILIZER_TYPE_AMOUNT.TOOLTIP",
            })}
            adornment={barnOutputMassUnit}
            type="number"
            disabled
            required={required}
            defaultValue={fertilizerData?.amount}
          />
        </DsmGrid>
      ) : (
        <DsmGrid className={classes.dsmGridTwoColumn}>
          <DsmGrid className={classes.dsmGridOneColumn}>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.amount`}
              label={formatLabelName(fertilizerData?.type as string)}
              type="number"
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.OPERATIONS.SHRIMP.FERTILIZER_TYPE_AMOUNT.TOOLTIP",
              })}
              metricUnit={barnOutputMassUnit}
              baseline={fertilizerData?.amount as number}
              disabled={formType === FormType.View || !editEnabled}
              displayTypeSelect={editEnabled}
            />
          </DsmGrid>
        </DsmGrid>
      )}
    </>
  );
};

export default ShrimpFertilizerForm;
